import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const PolygonMap = ({ wkt }) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);

  const parseWktToGeoJson = (wkt) => {
    if (!wkt || typeof wkt !== 'string') return null;

    const wktUpper = wkt.toUpperCase().trim();

    if (wktUpper.startsWith('POINT')) {
      // Extract coordinates from POINT (x y)
      const coordsMatch = wktUpper.match(/POINT\s*\(([^)]+)\)/);
      if (!coordsMatch) return null;

      const coordsStr = coordsMatch[1].trim();
      const [lng, lat] = coordsStr.split(/\s+/).map(Number);

      return {
        type: 'Point',
        coordinates: [lng, lat]
      };
    } else if (wktUpper.startsWith('LINESTRING')) {
      // Extract coordinates from LINESTRING (x y, x y, ...)
      const coordsMatch = wktUpper.match(/LINESTRING\s*\(([^)]+)\)/);
      if (!coordsMatch) return null;

      const coordsStr = coordsMatch[1];
      const coordPairs = coordsStr.split(',').map(pair => pair.trim().split(/\s+/).map(Number));

      // Convert to GeoJSON format [[lng, lat], ...]
      const coordinates = coordPairs.map(([lng, lat]) => [lng, lat]);

      return {
        type: 'LineString',
        coordinates: coordinates
      };
    } else if (wktUpper.startsWith('POLYGON')) {
      // Extract coordinates from POLYGON ((x y, x y, ...))
      const coordsMatch = wktUpper.match(/POLYGON\s*\(\(([^)]+)\)\)/);
      if (!coordsMatch) return null;

      const coordsStr = coordsMatch[1];
      const coordPairs = coordsStr.split(',').map(pair => pair.trim().split(/\s+/).map(Number));

      // Convert to GeoJSON format [[lng, lat], ...]
      const coordinates = coordPairs.map(([lng, lat]) => [lng, lat]);

      return {
        type: 'Polygon',
        coordinates: [coordinates]
      };
    } else if (wktUpper.startsWith('MULTIPOLYGON')) {
      // Extract coordinates from MULTIPOLYGON (((x y, x y)), ((x y, x y)))
      const polygonsMatch = wktUpper.match(/MULTIPOLYGON\s*\((.+)\)/);
      if (!polygonsMatch) return null;

      const polygonsStr = polygonsMatch[1];
      // Split by ')), ((' to get individual polygons
      const polygonParts = polygonsStr.split(/\)\),\s*\(\(/).map(p => p.replace(/^\(+|\)+$/g, ''));

      const coordinates = polygonParts.map(polygonStr => {
        const coordPairs = polygonStr.split(',').map(pair => pair.trim().split(/\s+/).map(Number));
        return coordPairs.map(([lng, lat]) => [lng, lat]);
      });

      return {
        type: 'MultiPolygon',
        coordinates: coordinates.map(poly => [poly]) // Each polygon has one ring
      };
    }

    return null;
  };

  useEffect(() => {
    if (wkt && mapContainer.current && !mapRef.current) {
      try {
        const geometry = parseWktToGeoJson(wkt);
        if (!geometry) {
          console.error('Failed to parse WKT to GeoJSON');
          return;
        }
        if (geometry.type === 'Polygon') {
          // Fix winding order for polygons (GeoJSON requires counterclockwise for outer rings)
          geometry.coordinates = geometry.coordinates.map(ring => ring.slice().reverse());
        } else if (geometry.type === 'MultiPolygon') {
          // Fix winding order for each polygon in MultiPolygon
          geometry.coordinates = geometry.coordinates.map(polygon =>
            polygon.map(ring => ring.slice().reverse())
          );
        }

        const map = new maplibregl.Map({
          container: mapContainer.current,
          style: {
            version: 8,
            sources: {
              'osm': {
                type: 'raster',
                tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors'
              }
            },
            layers: [
              {
                id: 'osm-layer',
                type: 'raster',
                source: 'osm'
              }
            ]
          },
          center: [-74, 4],
          zoom: 5
        });

        mapRef.current = map;

        map.on('load', () => {
          map.addSource('project-geometry', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: geometry,
              properties: {}
            }
          });

          // Add layers based on geometry type
          if (geometry.type === 'Point') {
            // Render Point as a circle
            map.addLayer({
              id: 'project-point',
              type: 'circle',
              source: 'project-geometry',
              paint: {
                'circle-radius': 10,
                'circle-color': '#ff0000',
                'circle-opacity': 0.8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }
            });
            // Fly to point location
            map.flyTo({ center: geometry.coordinates, zoom: 12 });
          } else if (geometry.type === 'LineString') {
            // Render LineString as a line
            map.addLayer({
              id: 'project-line',
              type: 'line',
              source: 'project-geometry',
              paint: {
                'line-color': '#00ff00',
                'line-width': 3
              }
            });
            // Fit bounds to LineString
            const bounds = new maplibregl.LngLatBounds();
            geometry.coordinates.forEach(coord => {
              bounds.extend(coord);
            });
            try {
              map.fitBounds(bounds, { padding: 20 });
            } catch (error) {
              console.warn('Could not fit bounds:', error);
            }
          } else {
            // Render Polygon/MultiPolygon with fill and border
            map.addLayer({
              id: 'project-fill',
              type: 'fill',
              source: 'project-geometry',
              paint: {
                'fill-color': '#00ff00',
                'fill-opacity': 0.5
              }
            });

            map.addLayer({
              id: 'project-borders',
              type: 'line',
              source: 'project-geometry',
              paint: {
                'line-color': '#000000',
                'line-width': 2
              }
            });

            // Fit map to the geometry bounds
            const bounds = new maplibregl.LngLatBounds();
            if (geometry.type === 'Polygon') {
              geometry.coordinates[0].forEach(coord => {
                bounds.extend(coord);
              });
            } else if (geometry.type === 'MultiPolygon') {
              geometry.coordinates.forEach(polygon => {
                polygon[0].forEach(coord => {
                  bounds.extend(coord);
                });
              });
            }
            try {
              map.fitBounds(bounds, { padding: 20 });
            } catch (error) {
              console.warn('Could not fit bounds:', error);
            }
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [wkt]);

  return (
    <div
      ref={mapContainer}
      style={{ width: '100%', height: '400px', border: '1px solid #ccc' }}
    />
  );
};

export default PolygonMap;
